import type {Thread} from '@github-ui/diff-lines/conversation-types'
import {CommentIcon} from '@primer/octicons-react'
import {ForYouItem} from '../../shared/ForYouItem'
import {ForYouLeadingIcon} from '../../shared/ForYouLeadingIcon'
import {getThreadAuthor, ThreadGroupVariantLabels, ThreadGroupVariant} from '../util/classify-thread'
import {ExpandedThread} from './ExpandedThread'
import {ThreadActions} from './ThreadActions'
import {ThreadTitle} from './ThreadTitle'
import {ThreadSnippet} from './ThreadSnippet'
import {getCommentSnippet, getInitialComment} from '../util/thread-comment-accessors'

export function ForYouThreadItem({thread, variant}: {thread: Thread; variant: ThreadGroupVariant}) {
  return (
    <ForYouItem
      key={thread.id}
      itemName={threadItemName(variant)}
      title={<ThreadTitle thread={thread} variant={variant} />}
      ariaLabel={threadTitleAriaLabel(thread, variant)}
      leadingVisual={<ForYouLeadingIcon icon={CommentIcon} />}
      actionSlot={<ThreadActions thread={thread} />}
      padding="none"
      description={<ThreadSnippet thread={thread} />}
    >
      <ExpandedThread thread={thread} variant={variant} />
    </ForYouItem>
  )
}

function getThreadMessage(thread: Thread | null | undefined, variant: ThreadGroupVariant) {
  if (variant === ThreadGroupVariant.Replied || variant === ThreadGroupVariant.YouReplied) {
    const initialComment = thread ? getInitialComment(thread) : undefined
    if (initialComment) {
      return `replied to "${getCommentSnippet(initialComment)}"`
    }
  }

  return ThreadGroupVariantLabels[variant] || 'commented'
}

function threadTitleAriaLabel(thread: Thread | null | undefined, variant: ThreadGroupVariant) {
  const message = getThreadMessage(thread, variant)
  // Falls back to the ghost user when the comment author no longer exists.
  const author = getThreadAuthor(thread, variant)

  return `${author.login} ${message}`
}

function threadItemName(variant: ThreadGroupVariant) {
  switch (variant) {
    case ThreadGroupVariant.Replied:
      return 'replied-comment'
    case ThreadGroupVariant.Mentioned:
      return 'mentioned-comment'
    case ThreadGroupVariant.Resolved:
      return 'resolved-comment'
    case ThreadGroupVariant.Commented:
    default:
      return 'commented-comment'
  }
}
